import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import HomePage from "./pages/HomePage";
import ErrorPage from "./pages/ErrorPage";
import InvoiceAddPage from "./pages/InvoiceAddPage";
import InvoiceEditPage from "./pages/InvoiceEditPage";
import InvoiceListPage from "./pages/InvoiceListPage";
import InvoicePreviewPage from "./pages/InvoicePreviewPage";
import SignInPage from "./pages/SignInPage";
import RouteScrollToTop from "./helper/RouteScrollToTop";
import { useEffect, useContext, useState } from "react";
import { ConfigContext } from "../src/context/GlobalContext";
import CustomerCurrencyMigrationModal from "./components/CustomerCurrencyMigrationModal";
import ExpenseCurrencyMigrationModal from "./components/ExpenseCurrencyMigrationModal";
import BusinessListPage from "./pages/BusinessListPage";
import CustomerListPage from "./pages/CustomerListPage";
import SoldItemsListPage from "./pages/SoldItemsListPage";
import BusinessAddPage from "./pages/BusinessAddPage";
import BusinessEditPage from "./pages/BusinessEditPage";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { ToastContainer } from "react-toastify";
import BusinessViewPage from "./pages/BusinessViewPage";
import CustomerEditPage from "./pages/CustomerEditPage";
import SessionPage from "./pages/SessionPage";
import CurrencyPage from "./pages/CurrencyPage";
import PaymentPage from "./pages/PaymentDataPage";
import { GetAllData } from "./data";
import PricingPlansPage from "./pages/PricingPlansPage";
import DownloadAppPage from "./pages/DownloadAppPage";
import CustomerViewPage from "./pages/CustomerViewPage";
import { SuccessPage } from "./pages/SuccessPage";
import ExpenseListPage from "./pages/ExpenseListPage";
import ExpenseAddPage from "./pages/ExpenseAddPage";
import ExpenseEditPage from "./pages/ExpenseEditPage";
import SalesReturnListPage from "./pages/SalesReturnListPage";
import ExpenseViewPage from "./pages/ExpenseViewPage";
import SaleReturnEditPage from "./pages/SaleReturnEditPage";
import UserPage from "./pages/UserPage";
import SaleReturnAddPage from './pages/SaleReturnAddPage'
import CreateInvoiceByAIPage from "./pages/CreateInvoiceByAIPage";
import ReportPage from "./pages/ReportPage";
import { trackUserEngagement } from "./Utils/EventTracker";
import ProformaInvoiceListPage from "./pages/ProformaInvoiceListPage";
import ProformaInvoiceAddPage from "./pages/ProformaInvoiceAddPage";
import ProformaInvoiceEditPage from "./pages/ProformaInvoiceEditPage";
import ProformaInvoiceViewPage from "./pages/ProformaInvoiceViewPage";
import QuotationAddPage from "./pages/QuotationAddPage";
import QuotationEditPage from "./pages/QuotationEditPage";
import QuotationViewPage from "./pages/QuotationViewPage";
import QuotationListPage from "./pages/QuotationListPage";
import ThankYouPage from "./pages/ThankYouPage";
import { ReferralPage } from "./pages/ReferralPage";
import CustomerLedgerPage from "./pages/CustomerLedgerPage";
import * as Sentry from "@sentry/react";
import { AgencyMembersAddPage } from "./pages/AgencyMembersAddPage";
import Gstr1Page from "./pages/Gstr1Page";
import Gstr3BPage from "./pages/Gstr3BPage";
import Gstr2APage from "./pages/Gstr2APage";

function App() {

  const { state, dispatch } = useContext(ConfigContext);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrationCustomers, setMigrationCustomers] = useState([]);
  const [businessCurrency, setBusinessCurrency] = useState("INR");
  const [dismissedBusiness, setDismissedBusiness] = useState(null);

  const [showExpenseMigrationModal, setShowExpenseMigrationModal] = useState(false);
  const [migrationExpenses, setMigrationExpenses] = useState([]);
  const [dismissedExpenseBusiness, setDismissedExpenseBusiness] = useState(null);

  useEffect(() => {
    GetAllData(state, dispatch)
    trackUserEngagement(state?.USERID, new Date().getTime(), "website", "website")
  }, [])

  // Currency migration check effect
  useEffect(() => {
    if (!state?.CUSTOMERS || !state?.SELECTEDBUSINESS || state.SELECTEDBUSINESS === "null") {
      setShowMigrationModal(false);
      return;
    }

    if (dismissedBusiness === state.SELECTEDBUSINESS) {
      setShowMigrationModal(false);
      return;
    }

    const currentBusiness = state?.BUSINESSES?.find(
      b => String(b.companyId) === String(state.SELECTEDBUSINESS)
    );
    const bizCurrency = currentBusiness?.currency || state?.CURRENCYCODE || "INR";
    setBusinessCurrency(bizCurrency);

    const legacyCustomers = state.CUSTOMERS.filter(
      cust => String(cust.companyId) === String(state.SELECTEDBUSINESS) && (!cust.currency || cust.currency.trim() === "")
    );

    if (legacyCustomers.length > 0) {
      setMigrationCustomers(legacyCustomers);
      setShowMigrationModal(true);
    } else {
      setShowMigrationModal(false);
    }
  }, [state?.CUSTOMERS, state?.SELECTEDBUSINESS, state?.BUSINESSES, state?.CURRENCYCODE, dismissedBusiness]);

  // Expense currency migration check effect
  useEffect(() => {
    console.log("Expense Migration Effect Triggered", {
      hasExpenses: !!state?.EXPENSE,
      selectedBusiness: state?.SELECTEDBUSINESS,
      dismissedExpenseBusiness,
      showMigrationModal
    });
    
    if (!state?.EXPENSE || !state?.SELECTEDBUSINESS || state.SELECTEDBUSINESS === "null") {
      setShowExpenseMigrationModal(false);
      return;
    }

    if (dismissedExpenseBusiness === state.SELECTEDBUSINESS) {
      console.log("Expense Migration: Business dismissed.");
      setShowExpenseMigrationModal(false);
      return;
    }

    const legacyExpenses = state.EXPENSE.filter(
      exp => String(exp.companyId) === String(state.SELECTEDBUSINESS) && (!exp.currency || exp.currency.trim() === "")
    );
    
    console.log("Expense Migration: Legacy Expenses found:", legacyExpenses.length);

    if (showMigrationModal) {
      console.log("Expense Migration: Blocked because Customer Modal is open.");
      setShowExpenseMigrationModal(false);
      return;
    }

    if (legacyExpenses.length > 0) {
      console.log("Expense Migration: Triggering Expense Modal.");
      setMigrationExpenses(legacyExpenses);
      setShowExpenseMigrationModal(true);
    } else {
      console.log("Expense Migration: No legacy expenses.");
      setShowExpenseMigrationModal(false);
    }
  }, [state?.EXPENSE, state?.SELECTEDBUSINESS, dismissedExpenseBusiness, showMigrationModal]);

  return (
    <BrowserRouter>
      <ToastContainer
        style={{ marginTop: '60px' }} // Adjust top margin
        theme={localStorage.getItem('theme') == 'dark' ? 'dark' : 'light'}
        pauseOnHover={false}
        newestOnTop
        autoClose={1000}
      />
      <RouteScrollToTop />
      <CustomerCurrencyMigrationModal
        show={showMigrationModal}
        customers={migrationCustomers}
        businessCurrency={businessCurrency}
        onClose={() => {
          setShowMigrationModal(false);
          setDismissedBusiness(state.SELECTEDBUSINESS);
        }}
      />
      <ExpenseCurrencyMigrationModal
        show={showExpenseMigrationModal}
        expenses={migrationExpenses}
        businessCurrency={businessCurrency}
        onClose={() => {
          setShowExpenseMigrationModal(false);
          setDismissedExpenseBusiness(state.SELECTEDBUSINESS);
        }}
      />
      <Routes>

        <Route path="/" element={(state?.USERID || localStorage.getItem("isLocal") == "true") ? <Navigate to="/dashboard" replace /> : <SignInPage />} />

        {/* login */}
        <Route exact path='/login' element={<SignInPage />} />

        {/* Dashboard */}
        <Route exact path='/dashboard' element={<HomePage />} />

        {/* Business */}
        <Route exact path='/business-list' element={<BusinessListPage />} />
        <Route exact path='/business-add' element={<BusinessAddPage />} />
        <Route exact path='/business-edit' element={<BusinessEditPage />} />
        <Route exact path='/business-view' element={<BusinessViewPage />} />

        {/* Invoice */}
        <Route exact path='/invoice-add' element={<InvoiceAddPage />} />
        <Route exact path='/invoice-edit' element={<InvoiceEditPage />} />
        <Route exact path='/invoice-list' element={<InvoiceListPage />} />
        <Route exact path='/invoice-preview' element={<InvoicePreviewPage />} />

        {/* Customer */}
        <Route exact path='/customer-list' element={<CustomerListPage />} />
        <Route exact path='/customer-view' element={<CustomerViewPage />} />
        <Route exact path='/customer-edit' element={<CustomerEditPage />} />
        <Route exact path='/customer-ledger' element={<CustomerLedgerPage />} />


        {/* Sold Item */}
        <Route exact path='/sold-items-list' element={<SoldItemsListPage />} />

        {/* Expenss */}
        <Route exact path='/expense' element={<ExpenseListPage />} />
        <Route exact path='/expense-add' element={<ExpenseAddPage />} />
        <Route exact path='/expense-edit' element={<ExpenseEditPage />} />
        <Route exact path='/expense-view' element={<ExpenseViewPage />} />


        {/* Proforma Invoice */}
        <Route exact path='/proformainvoic' element={<ProformaInvoiceListPage />} />
        <Route exact path='/proformainvoic-add' element={<ProformaInvoiceAddPage />} />
        <Route exact path='/proformainvoic-edit' element={<ProformaInvoiceEditPage />} />
        <Route exact path='/proformainvoic-view' element={<ProformaInvoiceViewPage />} />

        {/* Quotation */}
        <Route exact path='/quotation' element={<QuotationListPage />} />
        <Route exact path='/quotation-add' element={<QuotationAddPage />} />
        <Route exact path='/quotation-edit' element={<QuotationEditPage />} />
        <Route exact path='/quotation-view' element={<QuotationViewPage />} />


        {/* sales-return */}
        <Route exact path='/sales-return' element={<SalesReturnListPage />} />
        <Route exact path='/sales-return-add' element={<SaleReturnAddPage />} />
        <Route exact path='/sales-return-edit' element={<SaleReturnEditPage />} />

        {/* Reports / GST */}
        <Route exact path='/gstr-1' element={<Gstr1Page />} />
        <Route exact path='/gstr-3b' element={<Gstr3BPage />} />
        {/* <Route exact path='/gstr-2a' element={<Gstr2APage />} /> */}


        {/* Currency */}
        <Route exact path='/currency' element={<CurrencyPage />} />

        {/* Sessions */}
        <Route exact path='/sessions' element={<SessionPage />} />

        {/* Payment */}
        <Route exact path='/payment' element={<PaymentPage />} />

        {/* Pricing */}
        <Route exact path='/pricing' element={<PricingPlansPage />} />

        {/* Download app */}
        <Route exact path='/download-app' element={<DownloadAppPage />} />

        {/* Thank you */}
        <Route exact path='/thank-you' element={<ThankYouPage />} />

        {/* referral page*/}
        <Route exact path='/referral' element={<ReferralPage />} />

        {/* Members */}
        <Route exact path='/members' element={<AgencyMembersAddPage />} />

        {/*  */}
        <Route exact path='*' element={<ErrorPage />} />

        {/* Success Page */}
        <Route exact path='/success' element={<SuccessPage />} />

        <Route exact path='/user_details' element={<UserPage />} />

        <Route exact path='/smart-invoice-maker' element={<CreateInvoiceByAIPage />} />

        {/* <Route exact path='/report' element={< ReportPage/>} /> */}
        <Route exact path='/report' element={< ReportPage />} />



      </Routes>
    </BrowserRouter>
  );
}

export default App;
